import Button from '@mui/material/Button';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import {
  cancelPlannedLessonBuild,
  retryPlannedLessonBuild,
} from '@platform/client';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import { useTrackedMutation } from '../actions/ActionsProvider';

import { ACTION_LABEL, actionsFor, placeOf } from './actions';

import type { RowAction } from './actions';
import type { RebuildRow } from './RebuildDialog';
import type { PublishTarget } from '../authoring/PublishDialog';
import type { PlannedLesson } from '@platform/schema/curriculum';

/**
 * One row's actions — the first as a button, the rest behind an overflow menu so the
 * table stays readable. Open details is its own column and is on every row. Text
 * buttons that never wrap, like Open details, keep every row one line tall whichever
 * action comes first — CURR-35.
 */
export function RowActions({
  row,
  onBuild,
  onPublish,
  onRebuild,
}: {
  row: PlannedLesson;
  onBuild: (lessonId: string) => void;
  onPublish: (target: PublishTarget) => void;
  onRebuild: (row: RebuildRow) => void;
}) {
  const queries = useQueryClient();
  const [menu, setMenu] = useState<HTMLElement | null>(null);
  const refresh = () =>
    queries.invalidateQueries({ queryKey: ['curriculum', 'lessons'] });

  const retry = useTrackedMutation(`Retry ${row.lessonId}`, {
    mutationFn: () =>
      retryPlannedLessonBuild({
        path: { lessonId: row.lessonId },
        throwOnError: true,
      }),
    onSuccess: refresh,
  });
  const cancel = useTrackedMutation(`Cancel ${row.lessonId}`, {
    mutationFn: () =>
      cancelPlannedLessonBuild({
        path: { lessonId: row.lessonId },
        throwOnError: true,
      }),
    onSuccess: refresh,
  });

  const lesson = row.builtLessonId;
  const run = (kind: RowAction): void => {
    setMenu(null);
    if (kind === 'build') onBuild(row.lessonId);
    if (kind === 'retry') retry.mutate();
    if (kind === 'cancel') cancel.mutate();
    if ((kind === 'publish' || kind === 'unpublish') && lesson !== null)
      onPublish({
        lesson,
        action: kind,
        name: `${row.lessonId} · ${row.name}`,
        place: placeOf(row),
      });
    if (kind === 'rebuild') onRebuild(row);
  };

  const [first, ...rest] = actionsFor(row);
  if (first === undefined) return null;

  const item = ({ kind, disabled }: { kind: RowAction; disabled: boolean }) => {
    // Try opens the player beside the table; Open in Lessons goes there.
    if ((kind === 'try' || kind === 'open') && lesson !== null)
      return (
        <MenuItem
          key={kind}
          component={RouterLink}
          to={`/authoring/lessons/${lesson}`}
          {...(kind === 'try' ? { target: '_blank', rel: 'noreferrer' } : {})}
        >
          {ACTION_LABEL[kind]}
        </MenuItem>
      );
    return (
      <MenuItem
        key={kind}
        disabled={disabled}
        onClick={() => {
          run(kind);
        }}
      >
        {ACTION_LABEL[kind]}
      </MenuItem>
    );
  };

  return (
    <Stack
      direction="row"
      spacing={0.5}
      sx={{ alignItems: 'center', whiteSpace: 'nowrap' }}
    >
      <Button
        size="small"
        sx={{ whiteSpace: 'nowrap' }}
        disabled={first.disabled || retry.isPending || cancel.isPending}
        aria-label={`${ACTION_LABEL[first.kind]} ${row.lessonId}`}
        onClick={() => {
          run(first.kind);
        }}
      >
        {ACTION_LABEL[first.kind]}
      </Button>
      {rest.length > 0 && (
        <>
          <Button
            size="small"
            aria-label={`More actions for ${row.lessonId}`}
            aria-haspopup="menu"
            onClick={(event) => {
              setMenu(event.currentTarget);
            }}
          >
            More
          </Button>
          <Menu
            anchorEl={menu}
            open={menu !== null}
            onClose={() => {
              setMenu(null);
            }}
          >
            {rest.map(item)}
          </Menu>
        </>
      )}
    </Stack>
  );
}
